import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  X, Link2, XCircle, ExternalLink, RefreshCcw, Loader2, Info,
} from "lucide-react";

const fmtEur = (n) => new Intl.NumberFormat("es-ES", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(Number(n || 0));

/**
 * Modal que lista ofertas con `merge_suggestions[]` pendientes.
 * Permite al usuario Vincular o Rechazar cada sugerencia.
 * Sólo genera acciones cuando el usuario confirma explícitamente.
 */
export default function MergeSuggestionsModal({ open, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [dedupProgress, setDedupProgress] = useState(null); // {status, checked, total, ...}
  const [busyKey, setBusyKey] = useState(null); // `${oid}:${ext}` para deshabilitar botones

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/ofertas-comercial/merge-suggestions?limit=200`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("No se pudieron cargar las sugerencias", {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const triggerDedupe = async () => {
    setTriggering(true);
    setDedupProgress({ status: "queued", checked: 0, total: 0 });
    try {
      // Kick off background job (HTTP 202)
      const { data: kick } = await axios.post(`${API}/ofertas-comercial/dedupe-with-external`);
      const jobId = kick.job_id;
      // Poll cada 2s hasta done/failed
      while (true) {
        await new Promise(r => setTimeout(r, 2000));
        const { data: st } = await axios.get(`${API}/ofertas-comercial/dedupe-status/${jobId}`);
        setDedupProgress(st);
        if (st.status === "done") {
          toast.success("Barrido completado", {
            description: `${st.checked} ofertas · ${st.ofertas_with_suggestions} con sugerencias · ${st.total_suggestions} candidatos`,
          });
          await load();
          onChanged?.();
          break;
        }
        if (st.status === "failed") {
          toast.error("Barrido fallido", { description: st.error || "Sin detalle" });
          break;
        }
      }
    } catch (e) {
      toast.error("Error ejecutando el barrido", {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setTriggering(false);
      // Deja el progress visible 3s para que el usuario lo vea completado
      setTimeout(() => setDedupProgress(null), 3000);
    }
  };

  const link = async (oid, ext) => {
    const key = `${oid}:${ext.external_id}`;
    setBusyKey(key);
    try {
      await axios.post(`${API}/ofertas-comercial/${oid}/link-external`, { external_id: ext.external_id });
      toast.success("Vinculado", { description: `Oferta enlazada con ${ext.external_source}` });
      setItems(prev => prev.filter(i => i.id !== oid));
      onChanged?.();
    } catch (e) {
      toast.error("No se pudo vincular", {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setBusyKey(null);
    }
  };

  const reject = async (oid, ext) => {
    const key = `${oid}:${ext.external_id}`;
    setBusyKey(key);
    try {
      await axios.post(`${API}/ofertas-comercial/${oid}/reject-external`, { external_id: ext.external_id });
      toast.success("Sugerencia rechazada · caduca en 90 días");
      // Retirar del array local
      setItems(prev => prev
        .map(i => i.id === oid
          ? { ...i, merge_suggestions: i.merge_suggestions.filter(s => s.external_id !== ext.external_id) }
          : i)
        .filter(i => (i.merge_suggestions || []).length > 0));
      onChanged?.();
    } catch (e) {
      toast.error("No se pudo rechazar", {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="merge-suggestions-modal">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-xl font-['Outfit']">
                Sugerencias de fusión · PLACSP ↔ Ofertas manuales
              </DialogTitle>
              <DialogDescription className="mt-1 text-xs text-gray-600">
                Ofertas creadas manualmente que podrían coincidir con licitaciones detectadas por
                el scraper. Cada sugerencia lleva un score (0-100). Confirma o rechaza cada match.
              </DialogDescription>
            </div>
            <button
              data-testid="merge-suggestions-refresh"
              onClick={triggerDedupe}
              disabled={triggering}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#007934] text-white text-xs font-semibold hover:bg-[#005f28] disabled:opacity-50"
            >
              {triggering
                ? <Loader2 size={13} className="animate-spin" />
                : <RefreshCcw size={13} />}
              {triggering ? "Barriendo…" : "Recalcular ahora"}
            </button>
          </div>
        </DialogHeader>

        {dedupProgress && (
          <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3 mx-0 mb-2" data-testid="dedup-progress-bar">
            <div className="flex items-center justify-between text-[11px] mb-1.5">
              <span className="font-semibold text-indigo-800">
                {dedupProgress.status === "queued" && "En cola…"}
                {dedupProgress.status === "running" && `Analizando ${dedupProgress.checked?.toLocaleString("es-ES") || 0} / ${dedupProgress.total?.toLocaleString("es-ES") || "?"} ofertas`}
                {dedupProgress.status === "done" && "Barrido completado"}
                {dedupProgress.status === "failed" && "Barrido fallido"}
              </span>
              {dedupProgress.externals_count > 0 && (
                <span className="text-[10px] text-indigo-600 font-mono">
                  vs {dedupProgress.externals_count?.toLocaleString("es-ES")} externals
                </span>
              )}
            </div>
            <div className="h-1.5 bg-indigo-100 rounded overflow-hidden">
              <div
                className={`h-full transition-all ${
                  dedupProgress.status === "done"    ? "bg-emerald-500" :
                  dedupProgress.status === "failed"  ? "bg-rose-500"    :
                  "bg-indigo-500"
                }`}
                style={{ width: `${
                  dedupProgress.status === "done" ? 100 :
                  (dedupProgress.total > 0
                    ? Math.min(100, (dedupProgress.checked / dedupProgress.total) * 100)
                    : dedupProgress.status === "running" ? 5 : 0)
                }%` }}
                data-testid="dedup-progress-fill"
              />
            </div>
            {dedupProgress.status === "running" && dedupProgress.total_suggestions > 0 && (
              <div className="text-[10px] text-indigo-700 mt-1">
                Hasta ahora: <b>{dedupProgress.ofertas_with_suggestions}</b> ofertas con sugerencias · <b>{dedupProgress.total_suggestions}</b> candidatos
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto -mx-6 px-6" data-testid="merge-suggestions-list">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-gray-500">
              <Loader2 size={20} className="animate-spin mr-2" /> Cargando…
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-sm" data-testid="merge-suggestions-empty">
              <Info size={20} className="mx-auto mb-2 text-gray-400" />
              No hay sugerencias pendientes.<br />
              <span className="text-[11px] text-gray-400">
                El barrido corre automáticamente cada día a las 09:15 UTC.
              </span>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {items.map(o => (
                <div key={o.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid={`suggestion-oferta-${o.id}`}>
                  <div className="text-xs mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-900">{o.cliente || "(sin cliente)"}</span>
                      <span className="text-[10px] uppercase tracking-wider bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded font-semibold">
                        {o.estado_licitacion}
                      </span>
                      <span className="text-gray-600">·</span>
                      <span className="text-gray-700 font-mono text-[11px]">
                        {fmtEur(o.presupuesto)}
                      </span>
                      {o.fecha_presentacion && (
                        <>
                          <span className="text-gray-600">·</span>
                          <span className="text-gray-700 text-[11px]">
                            Pres. {String(o.fecha_presentacion).slice(0, 10)}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="text-gray-600 text-[11px] mt-1 leading-snug">
                      {o.objeto_contrato}
                    </div>
                  </div>

                  <div className="space-y-2 mt-3">
                    {(o.merge_suggestions || []).map(s => {
                      const key = `${o.id}:${s.external_id}`;
                      const busy = busyKey === key;
                      const scoreColor =
                        s.score >= 85 ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                        s.score >= 75 ? "bg-amber-100 text-amber-800 border-amber-300" :
                        "bg-gray-100 text-gray-700 border-gray-300";
                      return (
                        <div key={s.external_id} className="bg-white rounded border border-gray-200 p-3 flex gap-3 items-start" data-testid={`suggestion-${o.id}`}>
                          <div className={`text-[11px] font-bold px-2 py-1 rounded border ${scoreColor}`}>
                            {Math.round(s.score)}
                          </div>
                          <div className="flex-1 min-w-0 text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] uppercase bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded font-bold">
                                {s.external_source}
                              </span>
                              <span className="text-gray-500 font-mono text-[10px]" title={`Value ${fmtEur(s.external_value_eur)}`}>
                                {fmtEur(s.external_value_eur)}
                              </span>
                              {s.external_deadline && (
                                <span className="text-gray-500 text-[10px]">
                                  Deadline {String(s.external_deadline).slice(0, 10)}
                                </span>
                              )}
                              {s.external_url && (
                                <a href={s.external_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-0.5 text-[10px] font-semibold">
                                  <ExternalLink size={9}/> Abrir
                                </a>
                              )}
                            </div>
                            <div className="text-gray-800 mt-0.5 leading-snug font-medium">
                              {s.external_title}
                            </div>
                            {s.external_organism && (
                              <div className="text-gray-500 text-[10px] mt-0.5">
                                {s.external_organism}
                              </div>
                            )}
                            <div className="text-[9px] text-gray-400 mt-1 font-mono">
                              title {(s.title_sim * 100).toFixed(0)}% · client {(s.client_sim * 100).toFixed(0)}% · value {(s.value_score * 100).toFixed(0)}% · date {(s.date_score * 100).toFixed(0)}%
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              data-testid={`link-btn-${o.id}`}
                              disabled={busy}
                              onClick={() => link(o.id, s)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white text-[10px] font-bold hover:bg-emerald-700 disabled:opacity-50"
                            >
                              {busy ? <Loader2 size={9} className="animate-spin"/> : <Link2 size={9}/>}
                              Vincular
                            </button>
                            <button
                              data-testid={`reject-btn-${o.id}`}
                              disabled={busy}
                              onClick={() => reject(o.id, s)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-gray-300 text-gray-700 text-[10px] font-semibold hover:bg-gray-100 disabled:opacity-50"
                            >
                              <XCircle size={9}/> Rechazar
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 pt-3 flex items-center justify-between text-[11px] text-gray-500">
          <span>Rechazos persisten 90 días · Auto-recalcula lunes-domingo 09:15 UTC</span>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1 px-3 py-1 text-gray-600 hover:text-gray-800"
            data-testid="merge-suggestions-close"
          >
            <X size={12}/> Cerrar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
